'use client';

import React, { useMemo, useState } from 'react';
import Image from 'next/image';
import Typography from '@ui/Typography';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Avatar from '@/components/next-ui/Avatar';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { NEKI_LOGO_S3_URL } from '@/constants/neki.constant';
import useConfirmDialogStore from '@/components/Dialogs/confirmDialog.store';
import { switchUserCommunity } from '@/actions/switch-community.action';
import { isEmpty } from 'lodash-es';
import { cx } from 'cva';
import { getEnvBaseUrl } from '@/utils/stringHelpers';

function getCommunities(user) {
  if (!isEmpty(user?.communities)) return user.communities;

  return [];
}

function CommunitiesList({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const { seekConfirmation } = useConfirmDialogStore();
  const nekiBaseUrl = getEnvBaseUrl();

  const communities = useMemo(() => getCommunities(user), [user]);

  const toggleList = () => setIsOpen((prev) => !prev);

  const getCommunityLogoUrl = (entity) =>
    entity.logoUrl ? getQualifiedImageUrl(entity.logoUrl) : NEKI_LOGO_S3_URL;

  const isCommunitySame = (id) => {
    return user.activeCommunityId === id;
  };

  const handleSwitchGroup = async (community) => {
    if (isCommunitySame(community.id)) {
      return;
    }
    const confirmed = await seekConfirmation(
      `Switch Community`,
      `Do you want to switch to ${community.name} community?`,
      'Yes, Go ahead',
    );
    if (confirmed) {
      switchUserCommunity(community.id);
    }
  };

  if (isEmpty(communities)) return null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4">
        <Image
          width={40}
          height={40}
          src="/assets/icons/my-nonprofit.svg"
          alt="my communities"
          className="h-10 w-10"
        />
        <Typography
          variant="h3"
          role="button"
          tabIndex={0}
          onClick={toggleList}
          onKeyDown={(e) => e.key === 'Enter' && toggleList()}
          className="flex items-center gap-2 text-left hover:cursor-pointer hover:text-text-hyperlink focus:outline-none">
          Communities
          {isOpen ? (
            <ChevronUp className="h-5 w-5 transition-transform duration-300" />
          ) : (
            <ChevronDown className="h-5 w-5 transition-transform duration-300" />
          )}
        </Typography>
      </div>

      <div
        className={cx(
          'ml-8 overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'mt-4' : 'max-h-0',
        )}>
        <Typography
          title="Explore Communities"
          component="a"
          href={`${nekiBaseUrl}/explore`}
          variant="subtitle1"
          target="_blank"
          className="mb-2 flex cursor-pointer items-center gap-4 font-medium hover:text-text-hyperlink">
          {/* <AiOutlinePlusCircle className="h-6 w-6" /> */}
          Explore Communities
        </Typography>

        {!isEmpty(communities) && (
          <Typography
            title="My Communities"
            variant="subtitle1"
            className="my-3 flex items-center gap-2 font-medium">
            My Communities
          </Typography>
        )}

        {!isEmpty(communities) && (
          <ul className="flex max-w-[250px] flex-col gap-4">
            {communities.map((c) => (
              <li
                title={c.name}
                key={c.id}
                className={cx(
                  'group flex items-center gap-4 truncate',
                  !isCommunitySame(c.id) && 'cursor-pointer',
                )}
                onClick={() => handleSwitchGroup(c)}>
                <Avatar
                  src={getCommunityLogoUrl(c)}
                  alt={c.name}
                  profile="company"
                  size="xs"
                />
                <Typography
                  variant="subtitle1"
                  component="span"
                  color="textSecondary"
                  className={cx(
                    'truncate font-medium',
                    !isCommunitySame(c.id) && 'group-hover:text-text-hyperlink',
                  )}>
                  {c.name}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default CommunitiesList;
